import { useCallback } from 'react';

import { Sim } from '@generated/gql/graphql';
import { BooleanSelection } from '@src/types/index';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  useTrackSignupMobileAddedToBasket,
  useTrackSignupMobileSelectionsSubmittedForOneSim,
} from '@tracking/trackingEvents';
import { buildOptimizelyEntity } from '@tracking/utils/buildOptimizelyEntity';

import { SimSettingsFormValues } from '../types';

const useMobileTracking = (selectedSims: Sim[]) => {
  const { setSignupMobileServicesContext, trackingContexts } =
    useTrackingContext();
  const trackMobileAddedToBasket = useTrackSignupMobileAddedToBasket();
  const trackSelectionsSubmitted =
    useTrackSignupMobileSelectionsSubmittedForOneSim();

  const trackMobileSaved = useCallback(() => {
    const mobileContext = {
      tariffs: selectedSims.map((sim) => sim.selectedTariff.name),
      count_mobile_numbers_to_keep: selectedSims.reduce((acc, { settings }) => {
        if (settings.porting?.mobileNumber === '') {
          return acc + 1;
        }

        return acc;
      }, 0),
      count_existing_services_to_cancel: selectedSims.reduce(
        (acc, { settings }) => {
          if (settings.porting?.mobileNumber !== '') {
            return acc + 1;
          }

          return acc;
        },
        0
      ),
      count_services_for_budget_control: selectedSims.reduce(
        (acc, { settings }) => {
          if (settings.budgetControl) {
            return acc + 1;
          }

          return acc;
        },
        0
      ),
    };

    setSignupMobileServicesContext(mobileContext);

    if (selectedSims.length > 0) {
      trackMobileAddedToBasket(
        {},
        {
          contexts: [...trackingContexts, ...buildOptimizelyEntity()],
        }
      );
    }
  }, [
    selectedSims,
    setSignupMobileServicesContext,
    trackMobileAddedToBasket,
    trackingContexts,
  ]);

  const trackSimSubmitted = useCallback(
    (values: SimSettingsFormValues) => {
      trackSelectionsSubmitted({
        service_number:
          selectedSims.findIndex((sim) => sim.id === values.simPlanId) + 1,
        keep_existing_mobile_number:
          values.isKeepNumber === BooleanSelection.Yes,
        cancel_existing_service: values.isCancelNumber === BooleanSelection.Yes,
        set_budget_control: values.budgetControl === BooleanSelection.Yes,
      });
    },
    [selectedSims, trackSelectionsSubmitted]
  );

  return { trackMobileSaved, trackSimSubmitted };
};

export { useMobileTracking };
